import { breakpoints, OpacityBlackColors } from '@zep-us/zus/tokens';
import { type Variants } from 'framer-motion';

export type AiBuilderShellTransitionState = 'opening' | 'open' | 'closing';

export type AiBuilderPageSurfaceState =
  | 'page'
  | 'morphing'
  | 'shell'
  | 'closing';

export type AiBuilderPageSurfaceMode =
  | 'page'
  | 'ai-shell-entering'
  | 'ai-shell'
  | 'page-restoring';

export const AI_BUILDER_CHAT_PANEL_WIDTH = 550;
export const AI_BUILDER_CHAT_FIXED_PANEL_BREAKPOINT = breakpoints.laptopSm;
export const AI_BUILDER_CHAT_SPLIT_PANEL_BREAKPOINT = breakpoints.tabletSm;
export const AI_BUILDER_CHAT_DRAWER_BREAKPOINT =
  AI_BUILDER_CHAT_SPLIT_PANEL_BREAKPOINT - 1;
/** 모바일 드로어가 뷰포트 상단에서 띄우는 오프셋(px). */
export const AI_BUILDER_CHAT_DRAWER_TOP_OFFSET = 95;
export const AI_BUILDER_SHELL_MARGIN = 16;
export const AI_BUILDER_SHELL_TRANSITION_DURATION_MS = 360;
export const AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION_MS = 240;
export const AI_BUILDER_SHELL_TRANSITION_DURATION =
  AI_BUILDER_SHELL_TRANSITION_DURATION_MS / 1000;
export const AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION =
  AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION_MS / 1000;
export const AI_BUILDER_SHELL_TRANSITION_EASE = 'easeInOut';

export type AiBuilderPageSurfaceRect = {
  height: number;
  left: number;
  top: number;
  width: number;
};

export const getAiBuilderEffectivePageSurface = ({
  mode,
  state,
  usesDrawerLayout,
}: {
  mode: AiBuilderPageSurfaceMode;
  state: AiBuilderPageSurfaceState;
  usesDrawerLayout: boolean;
}): {
  mode: AiBuilderPageSurfaceMode;
  state: AiBuilderPageSurfaceState;
} => {
  if (usesDrawerLayout) {
    return {
      mode: 'page',
      state: 'page',
    };
  }

  return {
    mode,
    state,
  };
};

export const getAiBuilderChatPanelWidth = (viewportWidth: number) => {
  if (viewportWidth >= AI_BUILDER_CHAT_FIXED_PANEL_BREAKPOINT) {
    return AI_BUILDER_CHAT_PANEL_WIDTH;
  }

  if (viewportWidth >= AI_BUILDER_CHAT_SPLIT_PANEL_BREAKPOINT) {
    return viewportWidth / 2;
  }

  return viewportWidth;
};

export const getAiBuilderPageSurfaceWidth = (viewportWidth: number) => {
  if (viewportWidth >= AI_BUILDER_CHAT_FIXED_PANEL_BREAKPOINT) {
    return viewportWidth - AI_BUILDER_CHAT_PANEL_WIDTH;
  }

  if (viewportWidth >= AI_BUILDER_CHAT_SPLIT_PANEL_BREAKPOINT) {
    return viewportWidth / 2;
  }

  return 0;
};

const AI_BUILDER_SHELL_BOX_SHADOW = `0 8px 20px ${OpacityBlackColors[2]}, 0 0 16px ${OpacityBlackColors[1]}`;

/** 뷰포트 전체를 덮는 page 상태 rect. */
export const getAiBuilderPageRect = ({
  height,
  width,
}: {
  height: number;
  width: number;
}): AiBuilderPageSurfaceRect => ({
  height,
  left: 0,
  top: 0,
  width,
});

/**
 * 페이지 표면 모프 애니메이션 타깃. 순수하게 (state, rect)만의 함수다.
 * morphing 상태는 [시작, 끝] 키프레임 배열로 page→shell 보간을 명시한다.
 */
export const getAiBuilderPageSurfaceAnimation = ({
  pageRect,
  shellRect,
  state,
}: {
  pageRect: AiBuilderPageSurfaceRect;
  shellRect: AiBuilderPageSurfaceRect;
  state: AiBuilderPageSurfaceState;
}) => {
  if (state === 'page') {
    return {
      borderRadius: 0,
      boxShadow: 'none',
      height: 'auto',
      left: 0,
      top: 0,
      width: 'auto',
    };
  }

  if (state === 'morphing') {
    return {
      borderRadius: [0, 16],
      boxShadow: ['none', AI_BUILDER_SHELL_BOX_SHADOW],
      height: [pageRect.height, shellRect.height],
      left: [pageRect.left, shellRect.left],
      top: [pageRect.top, shellRect.top],
      width: [pageRect.width, shellRect.width],
    };
  }

  const isClosing = state === 'closing';
  const surfaceRect = isClosing ? pageRect : shellRect;

  return {
    borderRadius: isClosing ? 0 : 16,
    boxShadow: isClosing ? 'none' : AI_BUILDER_SHELL_BOX_SHADOW,
    height: surfaceRect.height,
    left: surfaceRect.left,
    top: surfaceRect.top,
    width: surfaceRect.width,
  };
};

const AI_BUILDER_CHAT_PANEL_CLOSED_X = '100%';

/**
 * 데스크톱 사이드 패널의 X축 슬라이드 인/아웃 variants.
 * 모바일 드로어 레이아웃은 ZUS Drawer(vaul)가 자체 애니메이션을 소유한다.
 */
export const getAiBuilderChatPanelVariants = (): Variants => {
  return {
    closed: {
      opacity: 1,
      x: AI_BUILDER_CHAT_PANEL_CLOSED_X,
    },
    opening: {
      opacity: 1,
      x: 0,
      transition: {
        duration: AI_BUILDER_SHELL_TRANSITION_DURATION,
        ease: AI_BUILDER_SHELL_TRANSITION_EASE,
      },
    },
    open: {
      opacity: 1,
      x: 0,
    },
    closing: {
      opacity: 1,
      x: AI_BUILDER_CHAT_PANEL_CLOSED_X,
      transition: {
        duration: AI_BUILDER_SHELL_CLOSE_TRANSITION_DURATION,
        ease: AI_BUILDER_SHELL_TRANSITION_EASE,
      },
    },
  };
};

export const getAiBuilderPageSurfaceRect = ({
  height,
  width,
}: {
  height: number;
  width: number;
}): AiBuilderPageSurfaceRect => {
  const surfaceWidth = getAiBuilderPageSurfaceWidth(width);

  if (surfaceWidth <= 0) {
    return {
      height,
      left: 0,
      top: 0,
      width: 0,
    };
  }

  return {
    height: Math.max(height - AI_BUILDER_SHELL_MARGIN * 2, 1),
    left: AI_BUILDER_SHELL_MARGIN,
    top: AI_BUILDER_SHELL_MARGIN,
    width: Math.max(surfaceWidth - AI_BUILDER_SHELL_MARGIN * 2, 1),
  };
};
